import { assignmentRolesFromLocation } from '../views/Setup/TaskList/utils/assignmentRoles';
import { hydrateSignOffFromTask } from '../views/Project/TaskList/drawers/AddTaskDialog/addTaskDialogUtils';
import { MANAGE_USER_OP, USER_ROLE } from './constants';

export function collectRoleUnionFromCycleTasks(tasks, roleKey) {
  const ids = new Set();
  (tasks || []).forEach((task) => {
    const roles = rolesFromCycleTask(task);
    (roles[roleKey] || []).forEach((id) => ids.add(String(id)));
  });
  return ids;
}

/** Returns user IDs present in roleKey on all selected tasks. */
export function collectRoleIntersectionFromCycleTasks(tasks, roleKey) {
  const taskList = (tasks || []).filter(Boolean);
  if (!taskList.length) return new Set();

  let intersection = null;
  taskList.forEach((task) => {
    const roles = rolesFromCycleTask(task);
    const ids = new Set((roles[roleKey] || []).map(String).filter(Boolean));
    if (intersection === null) {
      intersection = ids;
    } else {
      intersection = new Set([...intersection].filter((id) => ids.has(id)));
    }
  });

  return intersection || new Set();
}

export function rolesFromCycleTask(task) {
  const signOff = hydrateSignOffFromTask(task || {});
  return {
    preparer: task?.preparer || [],
    approver: task?.approver || [],
    sign_off_approvers: signOff.sign_off_approvers || [],
  };
}

export function collectRoleUnionFromSetupTemplates(templates, roleKey) {
  const ids = new Set();
  (templates || []).forEach((template) => {
    (template?.location || []).forEach((loc) => {
      const roles = assignmentRolesFromLocation(loc);
      (roles[roleKey] || []).forEach((id) => ids.add(String(id)));
    });
  });
  return ids;
}

function toIdSet(list) {
  return new Set((list || []).map(String).filter(Boolean));
}

/** True when both lists contain the same user IDs (order-independent). */
export function areSameIdLists(a, b) {
  const left = toIdSet(a);
  const right = toIdSet(b);
  if (left.size !== right.size) return false;
  return [...left].every((id) => right.has(id));
}

/**
 * Build bulk role target from union + intersection baseline shown in the dialog.
 * Adds local picks on top of union; removes only ids dropped from the baseline chips.
 */
export function buildBulkRoleTargetIds({
  dirty = false,
  localIds = [],
  unionIds = [],
  intersectionBaselineIds = [],
}) {
  if (!dirty) {
    return [...toIdSet(unionIds)];
  }

  const union = toIdSet(unionIds);
  const local = toIdSet(localIds);
  const baseline = toIdSet(intersectionBaselineIds);
  const removedFromBaseline = [...baseline].filter((id) => !local.has(id));

  const target = new Set(union);
  removedFromBaseline.forEach((id) => target.delete(id));
  local.forEach((id) => target.add(id));

  return [...target];
}

export function collectChecklistIdsFromSelectedTasks(tasks) {
  return (tasks || [])
    .map((task) => task?._id)
    .filter((id) => id != null && id !== '')
    .map(String);
}

/**
 * Build cycle bulkmanageuser payload (existing single-user API contract).
 * filter._id must always be checklist task ids, never user ids.
 */
export function buildCycleBulkManageUserPayload({
  checklistIds,
  taskIds,
  usertype,
  userId,
  action = MANAGE_USER_OP.ASSOCIATE,
}) {
  const resolvedChecklistIds = (checklistIds || taskIds || [])
    .map(String)
    .filter(Boolean);

  return {
    data: [{
      userid: String(userId),
      usertype: String(usertype || '').toLowerCase(),
      type: action,
    }],
    filter: {
      type: 'checklist',
      _id: resolvedChecklistIds,
    },
  };
}

/**
 * Sync cycle bulk approver then sign-off roles sequentially.
 * checklistIds are frozen up front so filter._id never receives user ids.
 */
export async function syncCycleBulkApproverRoles({
  checklistIds,
  selectedTasks = [],
  approverIds = [],
  signOffIds = [],
  manageUser,
}) {
  const resolvedChecklistIds = (checklistIds?.length
    ? checklistIds
    : collectChecklistIdsFromSelectedTasks(selectedTasks)
  ).map(String).filter(Boolean);

  if (!resolvedChecklistIds.length) {
    throw new Error('No checklist tasks selected');
  }

  await syncBulkApproverRoles({
    approverIds,
    signOffIds,
    currentApproverUnion: collectRoleUnionFromCycleTasks(selectedTasks, 'approver'),
    currentSignOffUnion: collectRoleUnionFromCycleTasks(selectedTasks, 'sign_off_approvers'),
    currentApproverIntersection: collectRoleIntersectionFromCycleTasks(selectedTasks, 'approver'),
    currentSignOffIntersection: collectRoleIntersectionFromCycleTasks(selectedTasks, 'sign_off_approvers'),
    associate: (usertype, userId) =>
      manageUser({
        action: MANAGE_USER_OP.ASSOCIATE,
        usertype,
        userId,
        checklistIds: resolvedChecklistIds,
      }),
    remove: (usertype, userId) =>
      manageUser({
        action: MANAGE_USER_OP.REMOVE,
        usertype,
        userId,
        checklistIds: resolvedChecklistIds,
      }),
  });
}

/**
 * Plan ordered bulk role operations (one API call per entry).
 * L2→L1 transfers interleave remove-then-associate per user. Fresh L1 additions run
 * before L1→L2 transfer removals so a sole approver swap never transiently orphans L1.
 */
export function planBulkApproverRoleOps({
  approverIds = [],
  signOffIds = [],
  currentApproverUnion,
  currentSignOffUnion,
  currentApproverIntersection,
  currentSignOffIntersection,
}) {
  const targetApprover = toIdSet(approverIds);
  const targetSignOff = toIdSet(signOffIds);
  const currentApprover = cloneIdSet(currentApproverUnion);
  const currentSignOff = cloneIdSet(currentSignOffUnion);

  const approverAdditions = diffBulkAssociateIds(targetApprover, currentApproverIntersection);
  const approverRemovals = diffRemovals(currentApprover, targetApprover);
  const signOffAdditions = diffBulkAssociateIds(targetSignOff, currentSignOffIntersection);
  const signOffRemovals = diffRemovals(currentSignOff, targetSignOff);

  // Users moving L2→L1 or L1→L2 must leave the old role first, otherwise
  // associate hits SOD after the previous sequential call already saved.
  const transferToL1 = new Set(
    approverAdditions.filter((id) => currentSignOff.has(String(id)))
  );
  const transferToL2 = new Set(
    signOffAdditions.filter((id) => currentApprover.has(String(id)))
  );

  const ops = [];
  const queued = new Set();
  const pushOp = (type, usertype, userId) => {
    const id = String(userId);
    const key = `${type}:${usertype}:${id}`;
    if (queued.has(key)) return;
    queued.add(key);
    ops.push({ type, usertype, userId: id });
  };

  transferToL1.forEach((userId) => {
    pushOp(MANAGE_USER_OP.REMOVE, USER_ROLE.SIGN_OFF_APPROVER, userId);
    pushOp(MANAGE_USER_OP.ASSOCIATE, USER_ROLE.APPROVER, userId);
  });

  approverAdditions
    .filter((userId) => !transferToL1.has(String(userId)))
    .forEach((userId) => {
      pushOp(MANAGE_USER_OP.ASSOCIATE, USER_ROLE.APPROVER, userId);
    });

  transferToL2.forEach((userId) => {
    pushOp(MANAGE_USER_OP.REMOVE, USER_ROLE.APPROVER, userId);
    pushOp(MANAGE_USER_OP.ASSOCIATE, USER_ROLE.SIGN_OFF_APPROVER, userId);
  });

  signOffAdditions
    .filter((userId) => !transferToL2.has(String(userId)))
    .forEach((userId) => {
      pushOp(MANAGE_USER_OP.ASSOCIATE, USER_ROLE.SIGN_OFF_APPROVER, userId);
    });

  approverRemovals
    .filter((userId) => !transferToL2.has(String(userId)))
    .forEach((userId) => {
      pushOp(MANAGE_USER_OP.REMOVE, USER_ROLE.APPROVER, userId);
    });
  signOffRemovals
    .filter((userId) => !transferToL1.has(String(userId)))
    .forEach((userId) => {
      pushOp(MANAGE_USER_OP.REMOVE, USER_ROLE.SIGN_OFF_APPROVER, userId);
    });

  return ops;
}

/**
 * Sync approver and sign-off roles with a single ordered operation list (no duplicate API calls).
 * Runs every planned op; throws only when all ops fail. Partial success still refreshes upstream.
 */
export async function syncBulkApproverRoles({
  approverIds = [],
  signOffIds = [],
  currentApproverUnion,
  currentSignOffUnion,
  currentApproverIntersection,
  currentSignOffIntersection,
  associate,
  remove,
}) {
  const ops = planBulkApproverRoleOps({
    approverIds,
    signOffIds,
    currentApproverUnion,
    currentSignOffUnion,
    currentApproverIntersection,
    currentSignOffIntersection,
  });

  const errors = [];
  let successCount = 0;

  for (const op of ops) {
    try {
      if (op.type === MANAGE_USER_OP.ASSOCIATE) {
        await associate(op.usertype, op.userId);
      } else {
        await remove(op.usertype, op.userId);
      }
      successCount += 1;
    } catch (err) {
      errors.push(err);
    }
  }

  if (successCount === 0 && errors.length) {
    throw errors[0];
  }

  if (errors.length) {
    const detail = errors[0]?.response?.data?.message || errors[0]?.message || 'Some updates could not be applied';
    const partialError = new Error(detail);
    partialError.partialSuccess = true;
    partialError.failedCount = errors.length;
    partialError.successCount = successCount;
    throw partialError;
  }
}

/** @deprecated Use syncBulkApproverRoles — kept for existing imports. */
export const syncBulkApproverRolesParallel = syncBulkApproverRoles;

/**
 * Diff current vs target user list and call associate/remove per changed user.
 * Additions run before removals.
 */
export async function applyBulkRoleTarget({
  currentUnion,
  targetIds,
  associate,
  remove,
}) {
  const target = toIdSet(targetIds);
  const current = cloneIdSet(currentUnion);
  const removals = diffRemovals(current, target);
  const additions = diffAdditions(current, target);

  for (const userId of additions) {
    await associate(userId);
  }
  for (const userId of removals) {
    await remove(userId);
  }
}

/**
 * Sync preparer role across selected cycle tasks (associate/remove per changed user).
 */
export async function syncCycleBulkPreparerRoles({
  checklistIds,
  selectedTasks = [],
  preparerIds = [],
  manageUser,
}) {
  const resolvedChecklistIds = (checklistIds?.length
    ? checklistIds
    : collectChecklistIdsFromSelectedTasks(selectedTasks)
  ).map(String).filter(Boolean);

  if (!resolvedChecklistIds.length) {
    throw new Error('No checklist tasks selected');
  }

  const currentUnion = collectRoleUnionFromCycleTasks(selectedTasks, USER_ROLE.PREPARER);

  await applyBulkRoleTarget({
    currentUnion,
    targetIds: preparerIds,
    associate: (userId) =>
      manageUser({
        action: MANAGE_USER_OP.ASSOCIATE,
        usertype: USER_ROLE.PREPARER,
        userId,
        checklistIds: resolvedChecklistIds,
      }),
    remove: (userId) =>
      manageUser({
        action: MANAGE_USER_OP.REMOVE,
        usertype: USER_ROLE.PREPARER,
        userId,
        checklistIds: resolvedChecklistIds,
      }),
  });
}

function cloneIdSet(source) {
  if (source instanceof Set) {
    return new Set([...source].map(String).filter(Boolean));
  }
  return toIdSet(source);
}

function diffAdditions(current, target) {
  return [...target].filter((id) => !current.has(String(id)));
}

/** Bulk associate applies a user to every selected task — call when not on all tasks yet. */
function diffBulkAssociateIds(target, roleIntersection) {
  const intersection = cloneIdSet(roleIntersection);
  return [...target].filter((id) => !intersection.has(String(id)));
}

function diffRemovals(current, target) {
  return [...current].filter((id) => !target.has(String(id)));
}
